import load from '#core/load';
import g from '#core/global';
import { init_trigger, init_global_listeners } from '#element/tooltip/tooltip';


const ref_$ = '.footnote-ref, .citation';
const template_$ = '[data-footnote-tooltip-template]';
const body_$ = '[data-footnote-body]';
const content_$ = '.tooltip__content';
const jump_link_$ = '.footnote-ref[href^="#footnote-"], .tooltip.footnote .tooltip__anchor .anchor__link[href="#footnotes"]';
const item_id_prefix = 'footnote-';
const tip_id_prefix = 'footnote-tip-';
const trigger_c = 'tooltip__trigger';


const module = {

	/**
	 * Init module
	 */
	init() {
		if (load.ran('footnote')) return false;

		load.content(() => {
			build_all();
		});
	},

};

export default module;
module.init();


/**
 * Find every footnote reference in the document and convert each one into a
 * tooltip — using the scaffold rendered by `footnotes.twig` (cloned from a
 * `<template>`) so the markup matches the canonical anchor pattern.
 */
function build_all() {
	const $refs = document.querySelectorAll(ref_$);
	if ($refs.length === 0) return;

	const $template = document.querySelector(template_$);
	if (!$template) {
		// eslint-disable-next-line no-console
		console.warn(`[footnote] No tooltip template found (${template_$}).`);
		return;
	}

	$refs.forEach($ref => build_one($ref, $template));
	init_global_listeners();
	init_smooth_scroll();
}


/**
 * Upgrade a single footnote reference into a hover tooltip. Wraps the marker
 * in a clone of the tooltip scaffold, populates the body from the matching
 * `<li>`, sets the marker's `href` so clicking jumps to the entry, and hands
 * the upgraded trigger off to `tooltip.init_trigger` for behavior. No-op if
 * the marker has already been upgraded or has no matching item.
 *
 * @param {HTMLElement} $ref The minimal `<a class="footnote-ref">` element
 * @param {HTMLTemplateElement} $template The footnote tooltip template
 */
function build_one($ref, $template) {
	if ($ref.dataset.footnoteBuilt === g.true_s) return;

	const number = get_footnote_number($ref);
	if (!number) return;

	const $item = document.getElementById(item_id_prefix + number);
	if (!$item) {
		// eslint-disable-next-line no-console
		console.warn(`[footnote] No matching #${item_id_prefix}${number} found for footnote-ref "${number}".`);
		return;
	}

	const $clone = $template.content.firstElementChild.cloneNode(true);
	const $content = $clone.querySelector(content_$);
	const $body = $clone.querySelector(body_$);
	if (!$content || !$body) {
		// eslint-disable-next-line no-console
		console.warn('[footnote] Tooltip template is missing required structure.');
		return;
	}

	const tip_id = tip_id_prefix + number;
	$content.id = tip_id;
	$body.innerHTML = $item.innerHTML;

	$ref.setAttribute('href', '#' + item_id_prefix + number);
	$ref.setAttribute('aria-describedby', tip_id);
	$ref.setAttribute('data-footnote-ref', number);
	$ref.classList.add('footnote-ref');
	$ref.classList.add(trigger_c);

	$ref.parentNode.insertBefore($clone, $ref);
	$clone.insertBefore($ref, $content);

	$ref.dataset.footnoteBuilt = g.true_s;

	init_trigger($ref);
}


/**
 * Extract a numeric footnote reference from the current marker, supporting
 * both the current `.footnote-ref` markup and the older `.citation-N` shape.
 *
 * @param {HTMLElement} $ref
 * @returns {string|null}
 */
function get_footnote_number($ref) {
	const explicit = $ref.getAttribute('data-footnote-ref');
	if (explicit) return explicit;

	const id = $ref.getAttribute('id') || '';
	const id_match = id.match(/citation-(\d+)/);
	if (id_match?.[1]) return id_match[1];

	const text = $ref.textContent?.trim() || '';
	const text_match = text.match(/^(\d+)$/);
	if (text_match?.[1]) return text_match[1];

	return null;
}


/**
 * Smooth-scroll footnote jumps without affecting unrelated anchor links.
 */
function init_smooth_scroll() {
	document.querySelectorAll(jump_link_$).forEach(($link) => {
		if ($link.dataset.footnoteSmoothScroll === g.true_s) return;

		$link.dataset.footnoteSmoothScroll = g.true_s;
		$link.addEventListener(g.click_e, (e) => {
			const href = $link.getAttribute('href') || '';
			if (!href.startsWith('#')) return;

			const $target = document.querySelector(href);
			if (!$target) return;

			e.preventDefault();

			$target.scrollIntoView({
				behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
				block: 'start',
			});

			if (window.history?.pushState) {
				window.history.pushState(null, '', href);
			} else {
				window.location.hash = href;
			}
		});
	});
}
